import { useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDatabase,
  FiUser,
  FiUsers
} from "react-icons/fi";

const LeadsDashboard = () => {
  const [expandedSections, setExpandedSections] = useState({
    1: true,
    2: true,
    3: true,
    4: true,
  });

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Mock data based on your image
  const roundRobin = [
    { id: 1, name: "Meredith", position: 1, isNext: true },
    { id: 2, name: "Harley", position: 2 },
    { id: 3, name: "Kelly", position: 3 },
    { id: 4, name: "Tom", position: 4 },
    { id: 5, name: "Vishal", position: 5 },
    { id: 6, name: "Alec", position: 6 },
  ];

  const sections = [
    {
      id: 1,
      title: "1. UNASSIGNED BDS LEADS",
      count: 12,
      subtitle: "Leads held by BDS and not yet assigned to an advisor",
      color: "text-green-700",
      bgColor: "bg-green-50 border-green-200",
      leads: [
        {
          date: "05/08/26",
          contact: "Michael Anderson",
          property: "Industrial Building\nAtlanta, GA",
          source: "Referral - CPA",
          bds: "Alec Marks",
          lastContacted: "No activity yet",
          actions: "Assign"
        },
        {
          date: "05/08/26",
          contact: "Summit Holdings, LLC",
          property: "Retail Strip Center\nSoutheast U.S.",
          source: "Direct Mail",
          bds: "Jenna Collins",
          lastContacted: "No activity yet",
          actions: "Assign"
        },
        {
          date: "05/07/26",
          contact: "David Lee",
          company: "Dallas, TX",
          property: "Multi-Family",
          source: "Website Inquiry",
          bds: "Alec Marks",
          lastContacted: "No activity yet",
          actions: "Assign"
        },
      ]
    },
    {
      id: 2,
      title: "2. ADVISOR-GENERATED LEADS",
      count: 14,
      subtitle: "Leads created and owned by our advisors",
      color: "text-blue-700",
      bgColor: "bg-blue-50 border-blue-200",
      leads: [
        {
          date: "05/08/26",
          contact: "Blue Ridge Logistics",
          property: "Industrial\nNorth Carolina",
          source: "Network",
          owner: "Harley Miller",
          lastContacted: "05/09/26 9:15 AM"
        },
        {
          date: "05/08/26",
          contact: "Oakwood Ventures",
          company: "Nashville, TN",
          property: "Office",
          source: "Conference",
          owner: "Kelly Johnson",
          lastContacted: "05/07/26 4:30 PM"
        },
        {
          date: "05/08/26",
          contact: "Greenfield Partners",
          property: "Retail\nGeorgia",
          source: "Referral",
          owner: "Tom Johnson",
          lastContacted: "05/06/26 11:20 AM"
        },
      ]
    },
    {
      id: 3,
      title: "3. ASSIGNED BDS LEADS (0-90 DAYS)",
      count: 18,
      subtitle: "Leads assigned to advisors in the last 90 days",
      color: "text-orange-700",
      bgColor: "bg-orange-50 border-orange-200",
      leads: [
        {
          date: "05/09/26",
          assigned: "05/09/26",
          contact: "Chris Francy",
          property: "Dollar General\nDahlonega, GA",
          source: "Direct Mail",
          owner: "Harley Miller",
          lastContacted: "05/09/26 9:41 AM"
        },
        {
          date: "05/06/26",
          assigned: "05/06/26",
          contact: "Bill Carroll",
          property: "Dollar General\nTallassee, AL",
          source: "Met",
          owner: "Kelly Johnson",
          lastContacted: "05/06/26 8:05 AM"
        },
      ]
    },
    {
      id: 4,
      title: "4. OLDER LEADS (> 90 DAYS)",
      count: 27,
      subtitle: "Leads not contacted in over 90 days",
      color: "text-purple-700",
      bgColor: "bg-purple-50 border-purple-200",
      leads: [
        {
          date: "02/03/26",
          assigned: "02/10/26",
          contact: "Pinecrest Holdings",
          property: "Office\nOrlando, FL",
          source: "Website Inquiry",
          owner: "Harley Miller",
          lastContacted: "02/10/26 10:15 AM"
        },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Round Robin Order */}
        <div className="bg-white border rounded-2xl p-3 mb-3">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="head-2 dark-H">ROUND ROBIN ORDER</h2>
            </div>
            <div className="head-5">Next Up</div>
          </div>

          <div className="flex gap-4 overflow-x-auto pb-2">
            {roundRobin.map((advisor, idx) => (
              <div
                key={idx}
                className={`flex-1 min-w-[120px] p-4 rounded-xl border ${advisor.isNext ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center body-N">
                    {advisor.position}
                  </div>
                  <div>
                    <div className="body-N">{advisor.name}</div>
                    {advisor.isNext && <div className="head-5 green-H">NEXT UP</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lead Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-3">
          <div className="bg-white border rounded-2xl p-3 hover:shadow-sm transition-all">
            <div className="flex justify-between items-start">
              <FiDatabase className="w-6 h-6 text-green-600" />
              <div className="head-2 dark-H">12</div>
            </div>
            <div className="mt-4 body-N ">Unassigned BDS Leads</div>
          </div>

          <div className="bg-white border rounded-2xl p-3 hover:shadow-sm transition-all">
            <div className="flex justify-between items-start">
              <FiUsers className="w-6 h-6 text-blue-600" />
              <div className="head-2 dark-H">14</div>
            </div>
            <div className="mt-4 body-N ">Advisor-Generated Leads</div>
          </div>

          <div className="bg-white border rounded-2xl p-3 hover:shadow-sm transition-all">
            <div className="flex justify-between items-start">
              <FiClock className="w-6 h-6 text-orange-600" />
              <div className="head-2 dark-H">18</div>
            </div>
            <div className="mt-4 body-N ">Assigned Leads</div>
          </div>

          <div className="bg-white border rounded-2xl p-3 hover:shadow-sm transition-all">
            <div className="flex justify-between items-start">
              <FiAlertTriangle className="w-6 h-6 text-purple-600" />
              <div className="head-2 dark-H">27</div>
            </div>
            <div className="mt-4 body-N ">Older Leads</div>
          </div>

          <div className="bg-white border rounded-2xl p-3 hover:shadow-sm transition-all">
            <div className="flex justify-between items-start">
              <FiCheckCircle className="w-6 h-6 text-emerald-600" />
              <div className="head-2 dark-H">9</div>
            </div>
            <div className="mt-4 body-N ">In Pipeline (Converted)</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-3">
          {sections.map((section) => (
            <div key={section.id} className="bg-white border rounded-2xl overflow-hidden">
              {/* Section Header */}
              <div
                className={`px-8 py-5 flex items-center justify-between border-b cursor-pointer ${section.bgColor}`}
                onClick={() => toggleSection(section.id)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${section.color.replace('text-', 'bg-')}`}>
                    {section.id}
                  </div>
                  <div>
                    <div className="head-2 flex items-center gap-3">
                      {section.title}
                      <span className="text-sm bg-white px-3 py-1 rounded-full border font-medium text-gray-700">
                        {section.count}
                      </span>
                    </div>
                    <div className="body-N text-gray-500 mt-0.5">{section.subtitle}</div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-emerald-600 hover:underline cursor-pointer text-sm font-medium">
                    View all {section.count} leads →
                  </div>
                  {expandedSections[section.id] ? <FiChevronUp /> : <FiChevronDown />}
                </div>
              </div>

              {/* Table */}
              {expandedSections[section.id] && (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1000px] text-sm table-auto">  {/* Added min-w for better control */}
                    <thead>
                      <tr className="bg-gray-50 border-b text-left text-xs uppercase tracking-wider text-gray-500">
                        <th className="px-8 py-4 w-24">LEAD DATE</th>
                        <th className="px-4 py-4 w-52">CONTACT / COMPANY</th>     {/* Fixed width */}
                        <th className="px-4 py-4 w-80">PROPERTY / INTEREST</th>    {/* Wider but controlled */}
                        <th className="px-4 py-4 w-44">LEAD SOURCE</th>

                        {section.id === 1 ? (
                          <th className="px-4 py-4 w-40">BDS</th>
                        ) : (
                          <th className="px-4 py-4 w-40">LEAD OWNER</th>
                        )}

                        <th className="px-4 py-4 w-40">LAST CONTACTED</th>
                        <th className="px-8 py-4 text-right w-28">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {section.leads.map((lead, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-8 py-5 font-medium text-gray-700 whitespace-nowrap">
                            {lead.date}
                          </td>

                          <td className="px-4 py-5">
                            <div className="font-medium flex items-center gap-2">
                              <FiUser className="text-gray-400 flex-shrink-0" />
                              {lead.contact}
                            </div>
                            {lead.company && (
                              <div className="text-xs text-gray-500 mt-0.5 pl-6 ">
                                {lead.company}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-5 text-gray-700  whitespace-pre-line">
                            {lead.property}
                          </td>

                          <td className="px-4 py-5 text-gray-600 white-space: pre-wrap">
                            {lead.source}
                          </td>

                          <td className="px-4 py-5">
                            <div className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-xs whitespace-nowrap">
                              <FiUser className="flex-shrink-0" />
                              {section.id === 1 ? lead.bds : lead.owner}
                            </div>
                          </td>

                          <td className="px-4 py-5 text-gray-600 text-sm whitespace-nowrap">
                            {lead.lastContacted}
                          </td>

                          <td className="px-8 py-5 text-right">
                            {lead.actions ? (
                              <button className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-5 py-2 rounded-lg font-medium whitespace-nowrap">
                                {lead.actions}
                              </button>
                            ) : (
                              <button className="text-gray-400 hover:text-gray-600 text-xl">•••</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>


      </div>
    </div>
  );
};

export default LeadsDashboard;